import type { AdminAction, BudgetBalance } from "@repo/common";
import { db, getBudgetBalances, updateAdminAction, useAuth } from "@repo/ui";
import {
	collection,
	doc,
	getCountFromServer,
	onSnapshot,
	query,
	runTransaction,
	serverTimestamp,
	Timestamp,
	where,
} from "firebase/firestore";
import {
	AlertTriangle,
	CircleDollarSign,
	ExternalLink,
	RefreshCw,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { AdminCallStats } from "./AdminCallStats";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const callsRefreshIntervalMs = 5 * 60_000;
const budgetRefreshIntervalMs = 60 * 60_000;

type StatusPageConfig = {
	name: string;
	url: string;
	apiUrl: string;
	type: "statuspage" | "gcp";
};

type ExternalStatusState = {
	status: "loading" | "ok" | "warning" | "error" | "unknown";
	label: string;
	description?: string;
};

const statusPages: StatusPageConfig[] = [
	{
		name: "Twilio",
		url: "https://status.twilio.com/",
		apiUrl: "https://status.twilio.com/api/v2/status.json",
		type: "statuspage",
	},
	{
		name: "LiveKit",
		url: "https://status.livekit.io/",
		apiUrl: "https://status.livekit.io/api/v2/status.json",
		type: "statuspage",
	},
	{
		name: "OpenAI",
		url: "https://status.openai.com/",
		apiUrl: "https://status.openai.com/api/v2/status.json",
		type: "statuspage",
	},
	{
		name: "GitHub",
		url: "https://www.githubstatus.com/",
		apiUrl: "https://www.githubstatus.com/api/v2/status.json",
		type: "statuspage",
	},
	{
		name: "Firebase",
		url: "https://status.firebase.google.com/",
		apiUrl: "https://status.firebase.google.com/incidents.json",
		type: "gcp",
	},
	{
		name: "Google Cloud",
		url: "https://status.cloud.google.com/",
		apiUrl: "https://status.cloud.google.com/incidents.json",
		type: "gcp",
	},
];

const systemIndex = [
	{
		title: "Monitor",
		entries: [
			{ label: "Alerts", page: "alerts", detail: "Open incidents" },
			{
				label: "Failed events",
				page: "dead-letters",
				detail: "Delivery failures",
			},
			{ label: "Admin tasks", page: "admin-tasks", detail: "Staff work queue" },
		],
	},
	{
		title: "Customer records",
		entries: [
			{ label: "Companies", page: "companies", detail: "Accounts and access" },
			{
				label: "Onboarding",
				page: "assisted-onboarding",
				detail: "Assisted setup",
			},
			{ label: "Users", page: "users", detail: "People and roles" },
		],
	},
	{
		title: "Voice platform",
		entries: [
			{ label: "Agents", page: "agents", detail: "Deployments and revisions" },
			{ label: "Calls", page: "calls", detail: "Recordings and evaluations" },
			{ label: "Services", page: "services", detail: "Business configuration" },
		],
	},
	{
		title: "Investigate",
		entries: [
			{
				label: "Audit logs",
				page: "audit-logs",
				detail: "Administrative changes",
			},
			{ label: "Raw events", page: "events", detail: "Event payloads" },
			{ label: "T-CDRs", page: "tcdrs", detail: "Telephony records" },
		],
	},
];

function firestoreDate(value: unknown): Date | null {
	if (value instanceof Timestamp) return value.toDate();
	if (value instanceof Date) return value;
	if (typeof value !== "string") return null;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function Dashboard({
	onNavigate,
}: {
	onNavigate?: (page: string) => void;
}) {
	const { agents, companies, adminActions, subscriptions, dailyCallStats } =
		useData();
	const { user } = useAuth();
	const [budgetBalances, setBudgetBalances] = useState<BudgetBalance[]>([]);
	const [balancesLoading, setBalancesLoading] = useState(true);
	const [balancesError, setBalancesError] = useState<string | null>(null);
	const [callsLast24Hours, setCallsLast24Hours] = useState<number | null>(null);
	const [callsLast7Days, setCallsLast7Days] = useState<number | null>(null);
	const [callsLoading, setCallsLoading] = useState(true);
	const [callsError, setCallsError] = useState<string | null>(null);
	const [budgetLastRefreshedAt, setBudgetLastRefreshedAt] =
		useState<Date | null>(null);
	const [callsLastRefreshedAt, setCallsLastRefreshedAt] = useState<Date | null>(
		null,
	);
	const [openAlertsCount, setOpenAlertsCount] = useState<number | null>(null);
	const [cancellationToComplete, setCancellationToComplete] =
		useState<AdminAction | null>(null);
	const [completingCancellation, setCompletingCancellation] = useState(false);
	const [externalStatuses, setExternalStatuses] = useState<
		Record<string, ExternalStatusState>
	>({});
	const [externalStatusLoading, setExternalStatusLoading] = useState(true);

	const fetchExternalStatuses = useCallback(async () => {
		setExternalStatusLoading(true);
		const results: Record<string, ExternalStatusState> = {};
		await Promise.all(
			statusPages.map(async (page) => {
				try {
					const res = await fetch(page.apiUrl);
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					const json = await res.json();
					if (page.type === "statuspage") {
						const indicator = json?.status?.indicator;
						const description = json?.status?.description;
						if (indicator === "none") {
							results[page.name] = {
								status: "ok",
								label: "Operational",
								description: description || "All Systems Operational",
							};
						} else if (indicator === "minor") {
							results[page.name] = {
								status: "warning",
								label: "Degraded",
								description: description || "Partially Degraded Service",
							};
						} else if (indicator === "major" || indicator === "critical") {
							results[page.name] = {
								status: "error",
								label: "Outage",
								description: description || "Service Outage",
							};
						} else {
							results[page.name] = {
								status: "ok",
								label: description || "Operational",
							};
						}
					} else {
						const activeIncidents = Array.isArray(json)
							? json.filter((inc: { end?: string }) => !inc.end)
							: [];
						if (activeIncidents.length === 0) {
							results[page.name] = {
								status: "ok",
								label: "Operational",
								description: "All Systems Operational",
							};
						} else {
							const impactedServices = activeIncidents
								.map((inc: { service_name?: string }) => inc.service_name)
								.filter(Boolean)
								.join(", ");
							results[page.name] = {
								status: "warning",
								label: "Degraded",
								description: impactedServices
									? `Issues: ${impactedServices}`
									: "Active service issue",
							};
						}
					}
				} catch {
					results[page.name] = {
						status: "unknown",
						label: "Check status",
					};
				}
			}),
		);
		setExternalStatuses(results);
		setExternalStatusLoading(false);
	}, []);

	useEffect(() => {
		fetchExternalStatuses();
	}, [fetchExternalStatuses]);

	const completeCancellation = async (
		actionId: string,
		subscriptionId: string,
		companyId: string,
	) => {
		if (!user?.uid) {
			throw new Error("A signed-in system administrator is required.");
		}
		const subscription = subscriptions.find(
			(candidate) => candidate.id === subscriptionId,
		);
		if (!subscription || subscription.companyId !== companyId) {
			throw new Error("The subscription for this cancellation was not found.");
		}

		const subscriptionRef = doc(db, "subscriptions", subscriptionId);
		const actionRef = doc(db, "adminActions", actionId);
		const lockRef = doc(db, "subscriptionActiveLocks", companyId);
		const auditRef = doc(collection(db, "auditLogs"));
		await runTransaction(db, async (transaction) => {
			const [storedSubscription, storedAction, lock] = await Promise.all([
				transaction.get(subscriptionRef),
				transaction.get(actionRef),
				transaction.get(lockRef),
			]);
			if (
				!storedSubscription.exists() ||
				storedSubscription.data().companyId !== companyId
			) {
				throw new Error(
					"The subscription for this cancellation was not found.",
				);
			}
			if (
				!storedAction.exists() ||
				storedAction.data().status !== "open" ||
				storedAction.data().subscriptionId !== subscriptionId
			) {
				throw new Error("The cancellation action is no longer open.");
			}

			const now = serverTimestamp();
			transaction.update(subscriptionRef, {
				status: "canceled",
				cancelAtPeriodEnd: false,
				canceledAt: now,
				nextBillingDate: null,
				updatedAt: now,
			});
			transaction.update(actionRef, {
				status: "done",
				completedAt: now,
				completedBy: user.uid,
				updatedAt: now,
			});
			if (lock.data()?.subscriptionId === subscriptionId) {
				transaction.delete(lockRef);
			}
			transaction.set(auditRef, {
				timestamp: now,
				userId: user.uid,
				type: "subscription_canceled",
				companyId,
				data: { subscriptionId, adminActionId: actionId },
			});
		});
	};

	const completeRenewal = async (
		actionId: string,
		subscriptionId: string,
		companyId: string,
	) => {
		if (!user?.uid) {
			throw new Error("A signed-in system administrator is required.");
		}

		const subscriptionRef = doc(db, "subscriptions", subscriptionId);
		const actionRef = doc(db, "adminActions", actionId);
		const auditRef = doc(collection(db, "auditLogs"));
		await runTransaction(db, async (transaction) => {
			const [subscription, action] = await Promise.all([
				transaction.get(subscriptionRef),
				transaction.get(actionRef),
			]);
			if (
				!subscription.exists() ||
				subscription.data().companyId !== companyId
			) {
				throw new Error("The subscription for this renewal was not found.");
			}
			if (
				!action.exists() ||
				action.data().status !== "open" ||
				action.data().type !== "subscription_renewal"
			) {
				throw new Error("The renewal action is no longer open.");
			}

			const dueDate = firestoreDate(action.data().dueDate);
			const nextBillingDate = firestoreDate(
				subscription.data().nextBillingDate,
			);
			if (
				!dueDate ||
				!nextBillingDate ||
				nextBillingDate.getTime() <= dueDate.getTime()
			) {
				throw new Error(
					"Update the subscription's next billing date after verifying the renewal in Grow.",
				);
			}

			const now = serverTimestamp();
			transaction.update(actionRef, {
				status: "done",
				completedAt: now,
				completedBy: user.uid,
				updatedAt: now,
			});
			transaction.set(auditRef, {
				timestamp: now,
				userId: user.uid,
				type: "subscription_renewal_completed",
				companyId,
				data: { subscriptionId, adminActionId: actionId },
			});
		});
	};

	useEffect(() => {
		const q = query(
			collection(db, "monitoringIncidents"),
			where("state", "==", "open"),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				setOpenAlertsCount(snapshot.size);
			},
			(error) => {
				console.error("Error fetching open alerts count:", error);
			},
		);

		return () => unsubscribe();
	}, []);

	const pendingActions = adminActions.filter(
		(action) => action.status === "open",
	);
	const activeCount = agents.filter(
		(agent) => agent.status === "Active",
	).length;
	const formatRefreshTime = (value: Date | null) =>
		value ? value.toLocaleString() : "Not refreshed yet";

	useEffect(() => {
		let isActive = true;

		async function loadBudgetBalances() {
			setBalancesLoading(true);
			setBalancesError(null);

			try {
				const response = await getBudgetBalances({});
				if (!isActive) {
					return;
				}

				setBudgetBalances(response.data.balances);
				setBudgetLastRefreshedAt(new Date());
			} catch (error) {
				if (!isActive) {
					return;
				}

				console.error("Failed to load budget balances", error);
				setBalancesError("Failed to load live provider balances.");
			} finally {
				if (isActive) {
					setBalancesLoading(false);
				}
			}
		}

		void loadBudgetBalances();
		const intervalId = window.setInterval(() => {
			void loadBudgetBalances();
		}, budgetRefreshIntervalMs);

		return () => {
			isActive = false;
			window.clearInterval(intervalId);
		};
	}, []);

	useEffect(() => {
		let isActive = true;

		async function loadCallCounts() {
			setCallsLoading(true);
			setCallsError(null);

			try {
				if (companies.length === 0) {
					if (isActive) {
						setCallsLast24Hours(0);
						setCallsLast7Days(0);
						setCallsLastRefreshedAt(new Date());
					}
					return;
				}

				const now = Date.now();
				const last24Hours = Timestamp.fromDate(
					new Date(now - 24 * 60 * 60 * 1000),
				);
				const last7Days = Timestamp.fromDate(
					new Date(now - 7 * 24 * 60 * 60 * 1000),
				);

				const [counts24Hours, counts7Days] = await Promise.all([
					Promise.all(
						companies.map(async (company) => {
							if (!company.id) {
								return 0;
							}

							const callsQuery = query(
								collection(db, "companies", company.id, "calls"),
								where("createdAt", ">=", last24Hours),
							);
							const snapshot = await getCountFromServer(callsQuery);
							return snapshot.data().count;
						}),
					),
					Promise.all(
						companies.map(async (company) => {
							if (!company.id) {
								return 0;
							}

							const callsQuery = query(
								collection(db, "companies", company.id, "calls"),
								where("createdAt", ">=", last7Days),
							);
							const snapshot = await getCountFromServer(callsQuery);
							return snapshot.data().count;
						}),
					),
				]);

				if (!isActive) {
					return;
				}

				setCallsLast24Hours(
					counts24Hours.reduce((sum, count) => sum + count, 0),
				);
				setCallsLast7Days(counts7Days.reduce((sum, count) => sum + count, 0));
				setCallsLastRefreshedAt(new Date());
			} catch (error) {
				if (!isActive) {
					return;
				}

				console.error("Failed to load dashboard call counts", error);
				setCallsError("Failed to refresh call metrics.");
			} finally {
				if (isActive) {
					setCallsLoading(false);
				}
			}
		}

		void loadCallCounts();
		const intervalId = window.setInterval(() => {
			void loadCallCounts();
		}, callsRefreshIntervalMs);

		return () => {
			isActive = false;
			window.clearInterval(intervalId);
		};
	}, [companies]);

	return (
		<div className="admin-dashboard">
			<div className="admin-dashboard-intro">
				<div>
					<h1>Systems index</h1>
					<p>
						Find a platform area, then review the live work and activity below.
					</p>
				</div>
			</div>

			{onNavigate && (
				<section className="admin-systems-index" aria-label="Platform areas">
					{systemIndex.map((group) => (
						<div className="admin-systems-index-group" key={group.title}>
							<h2>{group.title}</h2>
							{group.entries.map((entry) => (
								<button
									type="button"
									key={entry.page}
									onClick={() => onNavigate(entry.page)}
									className="admin-systems-index-link"
								>
									<span>{entry.label}</span>
									<small>{entry.detail}</small>
								</button>
							))}
						</div>
					))}
				</section>
			)}

			<div
				className="admin-dashboard-snapshot"
				aria-label="Current operational snapshot"
			>
				<button
					type="button"
					disabled={!onNavigate}
					onClick={() => onNavigate?.("alerts")}
				>
					<span>Open alerts</span>
					<strong data-urgent={Boolean(openAlertsCount)}>
						{openAlertsCount ?? "—"}
					</strong>
				</button>
				<button
					type="button"
					disabled={!onNavigate}
					onClick={() => onNavigate?.("admin-tasks")}
				>
					<span>Pending actions</span>
					<strong data-urgent={pendingActions.length > 0}>
						{pendingActions.length}
					</strong>
				</button>
				<button
					type="button"
					disabled={!onNavigate}
					onClick={() => onNavigate?.("calls")}
				>
					<span>Calls · 24 hours</span>
					<strong>{callsLast24Hours ?? "—"}</strong>
				</button>
				<button
					type="button"
					disabled={!onNavigate}
					onClick={() => onNavigate?.("agents")}
				>
					<span>Active agents</span>
					<strong>{activeCount}</strong>
				</button>
			</div>

			{openAlertsCount !== null && openAlertsCount > 0 && (
				<Card className="admin-dashboard-alert border-red-200 bg-red-50/50">
					<CardHeader className="pb-3">
						<CardTitle className="text-red-900 flex items-center gap-2 font-bold">
							<AlertTriangle className="h-5 w-5 text-red-600 animate-pulse" />
							Active System Alerts ({openAlertsCount})
						</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
						<p className="text-sm text-red-700">
							There are currently {openAlertsCount} active system alerts that
							require manual review and closure to resume automated health
							reporting.
						</p>
						{onNavigate && (
							<Button
								size="sm"
								className="bg-red-600 hover:bg-red-700 text-white font-medium shrink-0 shadow-sm"
								onClick={() => onNavigate("alerts")}
							>
								View Alerts Page
							</Button>
						)}
					</CardContent>
				</Card>
			)}

			{pendingActions.length > 0 && (
				<Card className="admin-dashboard-actions border-amber-200 bg-amber-50/50">
					<CardHeader className="pb-3">
						<CardTitle className="text-amber-900 flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-amber-600" />
							Pending Admin Actions ({pendingActions.length})
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{pendingActions.map((action) => {
							const company = companies.find(
								(candidate) => candidate.id === action.companyId,
							);
							return (
								<div
									key={action.id}
									className="flex flex-col justify-between gap-4 rounded-lg border border-amber-100 bg-white p-3 shadow-sm sm:flex-row sm:items-center"
								>
									<div>
										<p className="font-semibold text-slate-900">
											{action.title}
										</p>
										<p className="text-sm text-slate-600">
											Company:{" "}
											<span className="font-medium">
												{company?.name || action.companyId}
											</span>
											{action.subscriptionId && (
												<>
													{" "}
													| Subscription ID:{" "}
													<span className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
														{action.subscriptionId}
													</span>
												</>
											)}
											{action.callId && (
												<>
													{" "}
													| Call ID:{" "}
													<span className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
														{action.callId}
													</span>
												</>
											)}
										</p>
										{action.feedbackComment &&
											(action.feedbackComment.length > 240 ? (
												<details className="admin-action-details">
													<summary>View feedback details</summary>
													<p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
														{action.feedbackComment}
													</p>
												</details>
											) : (
												<p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
													{action.feedbackComment}
												</p>
											))}
										<p className="mt-1 text-xs text-slate-500">
											Requested:{" "}
											{action.createdAt
												? new Date(action.createdAt).toLocaleString()
												: "Unknown"}
										</p>
										{action.dueDate && (
											<p className="mt-1 text-xs text-slate-500">
												Renewal due:{" "}
												{new Date(action.dueDate).toLocaleDateString()}
											</p>
										)}
									</div>
									<div className="flex shrink-0 flex-wrap gap-2">
										{action.callId && (
											<Button size="sm" variant="outline" asChild>
												<a href={`/calls/${encodeURIComponent(action.callId)}`}>
													<ExternalLink className="h-4 w-4" />
													Review Call
												</a>
											</Button>
										)}
										<Button
											size="sm"
											variant="outline"
											className="border-amber-200 hover:bg-amber-50 hover:text-amber-900"
											onClick={async () => {
												try {
													if (!action.id) {
														throw new Error(
															"Admin action is missing identifiers.",
														);
													}
													if (action.type === "cancellation_requested") {
														if (!action.subscriptionId) {
															throw new Error(
																"Cancellation action is missing a subscription.",
															);
														}
														setCancellationToComplete(action);
													} else if (action.type === "subscription_renewal") {
														if (!action.subscriptionId) {
															throw new Error(
																"Renewal action is missing a subscription.",
															);
														}
														await completeRenewal(
															action.id,
															action.subscriptionId,
															action.companyId,
														);
														toast.success("Renewal reminder resolved.");
													} else {
														await updateAdminAction(db, action.id, {
															status: "done",
															completedAt: new Date().toISOString(),
															completedBy: user?.uid || null,
														});
													}
												} catch (error) {
													console.error(
														"Failed to update admin action:",
														error,
													);
													toast.error(
														error instanceof Error
															? error.message
															: "Failed to update admin action.",
													);
												}
											}}
										>
											{action.type === "subscription_renewal"
												? "Verify & Resolve"
												: "Mark Resolved"}
										</Button>
									</div>
								</div>
							);
						})}
					</CardContent>
				</Card>
			)}

			<Card className="admin-dashboard-external">
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle>External Status Pages</CardTitle>
						<p className="text-sm text-slate-600 mt-1">
							Live status monitoring and official status page links for key
							providers
						</p>
					</div>
					<Button
						size="sm"
						variant="ghost"
						onClick={fetchExternalStatuses}
						disabled={externalStatusLoading}
						className="flex items-center gap-1.5 text-xs text-slate-600"
					>
						<RefreshCw
							className={`h-3.5 w-3.5 ${externalStatusLoading ? "animate-spin" : ""}`}
						/>
						Refresh
					</Button>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
						{statusPages.map((service) => {
							const statusInfo = externalStatuses[service.name];
							const statusType =
								statusInfo?.status ||
								(externalStatusLoading ? "loading" : "unknown");

							return (
								<a
									key={service.name}
									href={service.url}
									target="_blank"
									rel="noreferrer"
									className="group min-w-0 rounded-xl border bg-white p-4 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 flex flex-col justify-between"
								>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0 flex-1">
											<p className="font-semibold text-slate-900 group-hover:text-primary transition-colors flex items-center gap-1.5">
												{service.name}
												<ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary transition-colors" />
											</p>
											<p className="text-xs text-slate-500 mt-1 line-clamp-1">
												{statusInfo?.description ||
													"Click to view full status page"}
											</p>
										</div>
										<Badge
											variant={
												statusType === "ok"
													? "secondary"
													: statusType === "warning"
														? "outline"
														: statusType === "error"
															? "destructive"
															: "outline"
											}
											className={
												statusType === "ok"
													? "bg-emerald-50 text-emerald-700 border-emerald-200"
													: statusType === "warning"
														? "bg-amber-50 text-amber-700 border-amber-200"
														: ""
											}
										>
											{statusType === "loading"
												? "Checking..."
												: statusType === "ok"
													? "Operational"
													: statusInfo?.label || "Unknown"}
										</Badge>
									</div>
								</a>
							);
						})}
					</div>
				</CardContent>
			</Card>

			<Card className="admin-dashboard-usage">
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle>Usage Stats</CardTitle>
						<p className="mt-1 text-sm text-slate-600">
							Live operational volume across agents and calls
						</p>
					</div>
					<div className="flex items-center gap-2 text-xs text-slate-500">
						<RefreshCw
							className={`h-4 w-4 ${callsLoading ? "animate-spin" : ""}`}
						/>
						<div className="text-right">
							<p>Refreshes every 5m</p>
							<p>Updated {formatRefreshTime(callsLastRefreshedAt)}</p>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					{callsError ? (
						<div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
							{callsError}
						</div>
					) : null}
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
						<div className="rounded-xl border bg-white p-4 shadow-sm">
							<p className="text-sm text-slate-600">Active agents</p>
							<p className="mt-2 text-3xl font-semibold text-slate-900">
								{activeCount}
							</p>
						</div>
						<div className="rounded-xl border bg-white p-4 shadow-sm">
							<p className="text-sm text-slate-600">Calls in last 24hr</p>
							<p className="mt-2 text-3xl font-semibold text-slate-900">
								{callsLast24Hours ?? "..."}
							</p>
						</div>
						<div className="rounded-xl border bg-white p-4 shadow-sm">
							<p className="text-sm text-slate-600">Calls in last 7d</p>
							<p className="mt-2 text-3xl font-semibold text-slate-900">
								{callsLast7Days ?? "..."}
							</p>
						</div>
						<div className="rounded-xl border bg-white p-4 shadow-sm flex flex-col justify-between">
							<div>
								<p className="text-sm text-slate-600">Active alerts</p>
								<p
									className={`mt-2 text-3xl font-semibold ${openAlertsCount && openAlertsCount > 0 ? "text-red-600 font-bold" : "text-slate-900"}`}
								>
									{openAlertsCount ?? "..."}
								</p>
							</div>
							{onNavigate &&
								openAlertsCount !== null &&
								openAlertsCount > 0 && (
									<Button
										size="sm"
										variant="link"
										className="px-0 h-auto text-left text-primary hover:text-primary/90 mt-2 self-start flex items-center gap-1 font-medium"
										onClick={() => onNavigate("alerts")}
									>
										View alerts <ExternalLink className="h-3.5 w-3.5" />
									</Button>
								)}
						</div>
					</div>
				</CardContent>
			</Card>

			<div className="admin-dashboard-call-stats">
				<AdminCallStats stats={dailyCallStats} />
			</div>

			<Card className="admin-dashboard-budget">
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle>Budget Monitoring</CardTitle>
						<p className="text-sm text-slate-600 mt-1">
							Live credit and balance visibility for external providers
						</p>
					</div>
					<div className="flex items-center gap-2 text-xs text-slate-500">
						<RefreshCw
							className={`h-4 w-4 ${balancesLoading ? "animate-spin" : ""}`}
						/>
						<div className="text-right">
							<p>Refreshes every 60m</p>
							<p>Updated {formatRefreshTime(budgetLastRefreshedAt)}</p>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					{balancesError ? (
						<div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
							{balancesError}
						</div>
					) : null}

					<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
						{budgetBalances.map((balance) => (
							<div
								key={`${balance.provider}-${balance.label}`}
								className="min-w-0 rounded-xl border bg-white p-4 shadow-sm"
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0 flex-1">
										<p className="text-sm text-slate-600">{balance.label}</p>
										<p className="mt-2 flex min-w-0 items-center gap-2 text-2xl font-semibold text-slate-900">
											<CircleDollarSign className="h-5 w-5 text-slate-500" />
											<span className="truncate">{balance.displayValue}</span>
										</p>
									</div>
									<Badge
										variant={
											balance.status === "ok"
												? "secondary"
												: balance.status === "unsupported"
													? "outline"
													: "destructive"
										}
									>
										{balance.status}
									</Badge>
								</div>
								{balance.message ? (
									<p className="mt-3 text-sm text-slate-600 break-words">
										{balance.message}
									</p>
								) : null}
								{balance.dashboardUrl ? (
									<a
										href={balance.dashboardUrl}
										target="_blank"
										rel="noreferrer"
										className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-slate-700 underline underline-offset-4"
									>
										Open dashboard
										<ExternalLink className="h-4 w-4" />
									</a>
								) : null}
								<p className="mt-3 text-xs text-slate-500">
									Updated {new Date(balance.updatedAt).toLocaleTimeString()}
								</p>
							</div>
						))}

						{!balancesLoading && budgetBalances.length === 0 ? (
							<div className="rounded-xl border border-dashed p-4 text-sm text-slate-500">
								No budget providers configured.
							</div>
						) : null}
					</div>
				</CardContent>
			</Card>

			<AlertDialog
				open={Boolean(cancellationToComplete)}
				onOpenChange={(open) => {
					if (!open && !completingCancellation) {
						setCancellationToComplete(null);
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Confirm subscription cancellation
						</AlertDialogTitle>
						<AlertDialogDescription>
							Confirm that the subscription was canceled in Grow. This will mark
							the local subscription as canceled, clear its next billing date,
							and complete the admin action.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={completingCancellation}>
							Back
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={completingCancellation}
							onClick={(event) => {
								event.preventDefault();
								const action = cancellationToComplete;
								if (!action?.id || !action.subscriptionId) return;
								setCompletingCancellation(true);
								completeCancellation(
									action.id,
									action.subscriptionId,
									action.companyId,
								)
									.then(() => setCancellationToComplete(null))
									.catch((error) => {
										console.error(
											"Failed to complete subscription cancellation:",
											error,
										);
									})
									.finally(() => setCompletingCancellation(false));
							}}
						>
							{completingCancellation
								? "Completing..."
								: "Confirm cancellation"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
